'use client';

import { useState, useEffect, useMemo } from 'react';
import { StaticDynamicLink as Link } from '@/lib/static-export-nav';
import { useAuthStore } from '@/lib/auth-store';
import { getEffectiveRole, isOrganizationStaffRole, isOrganizerRole } from '@/lib/rbac';
import { getCreateEventPath, userCanCreateEvents } from '@/lib/create-event';
import { Check, ChevronRight, X, Rocket } from 'lucide-react';

type OnboardingRole = 'organization_admin' | 'organizer' | 'individual_organizer' | 'participant';

const STEPS: Record<OnboardingRole, { id: string; label: string; href?: string; checkKey?: string }[]> = {
  organization_admin: [
    { id: 'profile', label: 'Complete organization profile', href: '/dashboard/organization/profile', checkKey: 'org_profile' },
    { id: 'organizers', label: 'Invite your first organizer', href: '/dashboard/organization/organizers', checkKey: 'org_organizers' },
    { id: 'event', label: 'Create your first event', checkKey: 'first_event' },
    { id: 'settings', label: 'Configure domain & settings', href: '/dashboard/organization/settings', checkKey: 'org_settings' },
  ],
  organizer: [
    { id: 'profile', label: 'Complete organizer profile', href: '/dashboard/profile', checkKey: 'organizer_profile' },
    { id: 'event', label: 'Create your first event', checkKey: 'first_event' },
    { id: 'tickets', label: 'Set up ticketing', checkKey: 'tickets' },
    { id: 'publish', label: 'Publish and share your event', checkKey: 'published' },
  ],
  individual_organizer: [
    { id: 'profile', label: 'Complete your profile', href: '/dashboard/profile', checkKey: 'organizer_profile' },
    { id: 'event', label: 'Create your first event', checkKey: 'first_event' },
    { id: 'publish', label: 'Publish your event', checkKey: 'published' },
  ],
  participant: [
    { id: 'profile', label: 'Complete your profile', href: '/dashboard/profile', checkKey: 'user_profile' },
    { id: 'browse', label: 'Discover an event', href: '/events', checkKey: 'browsed' },
    { id: 'register', label: 'Register for an event', checkKey: 'registered' },
  ],
};

const STORAGE_KEY = 'enthios_onboarding_progress';

function resolveRole(user: ReturnType<typeof useAuthStore.getState>['user']): OnboardingRole {
  const role = getEffectiveRole(user);
  if (isOrganizationStaffRole(role)) return 'organization_admin';
  if (role === 'individual_organizer') return 'individual_organizer';
  if (isOrganizerRole(role)) return 'organizer';
  return 'participant';
}

type Props = {
  stats?: {
    total_events?: number;
    owned_events?: number;
    active_events?: number;
    published_events_count?: number;
    total_registrations?: number;
    total_tickets_sold?: number;
    upcoming_events?: any[];
    recent_events?: any[];
  } | null;
};

export function OnboardingWizard({ stats }: Props = {}) {
  const { user } = useAuthStore();
  const [dismissed, setDismissed] = useState(false);
  const [completed, setCompleted] = useState<Record<string, boolean>>({});

  const role = resolveRole(user);
  const steps = STEPS[role] ?? STEPS.participant;
  const createEventHref = getCreateEventPath({
    isAuthenticated: true,
    canCreateEvents: userCanCreateEvents(user?.role),
    workspace: isOrganizationStaffRole(user?.role) ? 'organization' : 'personal',
  });

  useEffect(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) setCompleted(JSON.parse(raw));
      if (localStorage.getItem(`${STORAGE_KEY}_dismissed_${role}`)) setDismissed(true);
    } catch {
      /* ignore */
    }
  }, [role]);

  const dynamicCompleted = useMemo(() => {
    const dynamic: Record<string, boolean> = { ...completed };
    
    if (user?.name || user?.email || (user as any)?.organization_name) {
      dynamic.organizer_profile = true;
      dynamic.org_profile = true;
      dynamic.user_profile = true;
    }
    
    const totalEvents = Number(stats?.owned_events ?? stats?.total_events ?? ((stats?.upcoming_events?.length || 0) + (stats?.recent_events?.length || 0)));
    if (totalEvents > 0) {
      dynamic.first_event = true;
      dynamic.org_organizers = true;
    }
    
    const totalRegs = Number(stats?.total_registrations ?? stats?.total_tickets_sold ?? 0);
    if (totalRegs > 0 || totalEvents > 0) {
      dynamic.tickets = true;
      dynamic.registered = true;
      dynamic.browsed = true;
    }
    
    const activeEvents = Number(stats?.active_events ?? 0);
    if (activeEvents > 0 || totalEvents > 0) {
      dynamic.published = true;
      dynamic.org_settings = true;
    }
    
    return dynamic;
  }, [completed, stats, user]);

  const doneCount = steps.filter((s) => dynamicCompleted[s.checkKey ?? s.id]).length;
  const progress = Math.round((doneCount / steps.length) * 100);

  if (!user || dismissed || progress >= 100) return null;

  const markDone = (key: string) => {
    const next = { ...completed, [key]: true };
    setCompleted(next);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
  };

  const dismiss = () => {
    setDismissed(true);
    localStorage.setItem(`${STORAGE_KEY}_dismissed_${role}`, '1');
  };

  return (
    <div className="ud-onboard mb-1">
      <div className="ud-onboard-head">
        <div className="flex items-start gap-3 min-w-0">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[rgba(152,170,157,0.15)] text-[var(--color-primary-hover)]">
            <Rocket className="h-5 w-5" aria-hidden />
          </div>
          <div className="min-w-0">
            <p className="ud-kicker">Setup guide</p>
            <h2 className="ud-onboard-title">Welcome! Complete your setup</h2>
            <p className="mt-1 text-sm text-[var(--color-text-muted)]">
              {doneCount} of {steps.length} steps done · {progress}% complete
            </p>
          </div>
        </div>
        <button
          type="button"
          onClick={dismiss}
          className="rounded-lg p-1.5 text-[var(--color-text-muted)] transition-colors hover:bg-[var(--color-surface-muted)] hover:text-[var(--color-text)]"
          aria-label="Dismiss setup guide"
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      <div className="mb-4 h-2 overflow-hidden rounded-full bg-[var(--color-surface-muted)] ring-1 ring-[var(--color-border)]">
        <div
          className="h-full rounded-full bg-[var(--color-primary)] transition-all duration-500"
          style={{ width: `${progress}%` }}
        />
      </div>

      <ul className="grid gap-2 sm:grid-cols-2">
        {steps.map((step, index) => {
          const isDone = Boolean(dynamicCompleted[step.checkKey ?? step.id]);
          const href = step.id === 'event' ? createEventHref : step.href;

          return (
            <li
              key={step.id}
              className={`rounded-xl border p-3 transition-colors ${
                isDone
                  ? 'border-[var(--color-border)] bg-[var(--color-surface-muted)] opacity-80'
                  : 'border-[var(--color-border)] bg-[var(--color-surface)] hover:border-[var(--color-primary)]'
              }`}
            >
              <div className="flex items-start gap-3">
                <span
                  className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-xs font-semibold ${
                    isDone
                      ? 'bg-[rgba(90,143,110,0.15)] text-[var(--color-success)]'
                      : 'bg-[var(--color-surface-muted)] text-[var(--color-text-muted)] ring-1 ring-[var(--color-border)]'
                  }`}
                >
                  {isDone ? <Check className="h-3.5 w-3.5" /> : index + 1}
                </span>
                <div className="min-w-0 flex-1">
                  {href && !isDone ? (
                    <Link
                      href={href}
                      className="group inline-flex items-start gap-1 text-sm font-medium text-[var(--color-text)] transition-colors hover:text-[var(--color-primary-hover)]"
                    >
                      <span>{step.label}</span>
                      <ChevronRight className="mt-0.5 h-3.5 w-3.5 shrink-0 opacity-60 transition-transform group-hover:translate-x-0.5" />
                    </Link>
                  ) : (
                    <span
                      className={`text-sm ${isDone ? 'text-[var(--color-text-muted)] line-through' : 'font-medium text-[var(--color-text)]'}`}
                    >
                      {step.label}
                    </span>
                  )}
                  {!isDone ? (
                    <button
                      type="button"
                      onClick={() => markDone(step.checkKey ?? step.id)}
                      className="mt-1 block text-xs font-medium text-[var(--color-primary-hover)] hover:text-[var(--color-text)]"
                    >
                      Mark as done
                    </button>
                  ) : null}
                </div>
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
